/**
 * What the Slack integration page does outside React state, in a module of
 * its own: the page chunk every provider shares imports it for the hero
 * button, and must not pull in `slack-form` (`next/dynamic` keeps that chunk
 * out of every other provider's page).
 */

/**
 * The ids of the Slack sections' headings (`ProseSection id`): a button or a
 * finished action moves focus to one when the control that had it is gone.
 */
export const SLACK_SECTION_IDS = {
  workspace: 'slack-workspace',
  chat: 'slack-chat',
  notifications: 'slack-notifications',
  account: 'slack-account',
} as const

type SlackSection = keyof typeof SLACK_SECTION_IDS

/** Start Slack's install for the organization; Slack returns to this page. */
export function startSlackInstall(): void {
  const url = new URL(
    '/api/integrations/slack/oauth/authorize',
    window.location.origin
  )
  url.searchParams.set('returnUrl', window.location.pathname)
  window.location.assign(url.toString())
}

function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

/**
 * Move focus to a Slack section's heading; `isScrolled` also brings it to
 * the top of the page (the hero's Manage button), where a plain focus only
 * scrolls as far as it must.
 */
export function focusSlackSection(
  section: SlackSection,
  { isScrolled = false }: { isScrolled?: boolean } = {}
): void {
  const heading = document.getElementById(SLACK_SECTION_IDS[section])
  if (!heading) {
    return
  }
  if (isScrolled) {
    heading.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start',
    })
  }
  heading.focus({ preventScroll: isScrolled })
}

/**
 * Focus `node` unless the person has already put focus somewhere else: an
 * action that finishes after a Convex round trip must not steal it back.
 */
export function focusIfLost(node: HTMLElement): void {
  const active = document.activeElement
  if (!active || active === document.body || !active.isConnected) {
    node.focus()
  }
}
